import {
	ContextMenu,
	ContextMenuDeleteItem,
	IDataListColumn,
	PaginatedAutoDataList,
	SplitButton,
	isSuccess,
	laravelPaginatedListFetchConfig,
	useHasPermission,
	useLaravelDeleteEntity,
	useToasts,
} from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { PermissionName } from "../../../shared/types/permission";
import { integrationService } from "../shared/api/integrationService";
import { IntegrationIndexResource } from "../shared/types/integration";

export const IntegrationList: FC = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const { showToast } = useToasts();
	const canCreate = useHasPermission<PermissionName>(["integration.create"]);

	const deleteIntegration = useLaravelDeleteEntity(integrationService, {
		entityName: t("Integration"),
		message: t("Integration was successfully deleted."),
	});

	const handleCreateDreso = async () => {
		const response = await integrationService.create({ type: "dreso" });

		if (isSuccess(response)) {
			await queryClient.invalidateQueries({ queryKey: integrationService.indexKey });
			showToast({
				type: "success",
				title: t("Integration"),
				body: t("Integration was successfully created."),
			});
			navigate(`${response.data.data.id}`);
		}
	};

	const columns: IDataListColumn<IntegrationIndexResource>[] = useMemo(
		() => [
			{
				type: "value",
				valueKey: "type",
				heading: t("Type"),
			},
			{
				type: "actions",
				heading: "",
				size: "max-content",
				action: (integration) => (
					<ContextMenu displayShortcutsAs="icon">
						<ContextMenuDeleteItem
							action={() => deleteIntegration(integration.id)}
							title={t("Delete integration")}
							label={t("Delete integration")}
							heading={t("Delete")}
							labels={{ close: t("Close"), confirm: t("Confirm") }}
						>
							{t("Are you sure you want to delete this integration?")}
						</ContextMenuDeleteItem>
					</ContextMenu>
				),
			},
		],
		[deleteIntegration, t],
	);

	return (
		<PaginatedAutoDataList
			name="integrations"
			heading={t("Integrations")}
			headerActions={
				canCreate ? (
					<SplitButton size="small" color="primary" options={[{ title: "Dreso", onClick: handleCreateDreso }]} />
				) : undefined
			}
			empty={t("No integrations available")}
			columns={columns}
			listFetchConfig={laravelPaginatedListFetchConfig(integrationService.listKey, integrationService.index)}
			onRowClick={({ id }) => navigate(`${id}`)}
		/>
	);
};
